import { Label } from 'components/form'
import { capitalize, snakeCase, startCase } from 'lodash-es'
import PropTypes from 'prop-types'
import Form from 'react-bootstrap/Form'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

/**
 * Wraps up Form.Group, Form.Label, Form.Control and React-Hook-Form's register :)
 * @todo add ability to render checkbox/radio groups
 * @param label {string} May be inferred from the name
 * @param name {string} May be inferred from the label
 * @param type {'textarea'|string} Either "textarea" or any other `<input>` type
 * @param defaultValue {string|number} Raw default value. See `source`
 * @param required {boolean}
 * @param maxLength {number}
 * @param rules {object} Validation rules; see https://www.react-hook-form.com/advanced-usage#ErrorMessages
 * @param fieldProps {object} Any other prop is deconstructed directly into the Form.Control element - except the className, it goes into the Form.Group..
 * @return {JSX.Element}
 * @todo implement https://www.react-hook-form.com/advanced-usage#ConnectForm so register and errors can be skipped?
 */
export default function InputRow({ label, name, type, defaultValue, required, maxLength, rules = {}, ...fieldProps }) {
	const { t } = useTranslation()
	
	name  = name  || snakeCase(label)
	label = label || t(capitalize(startCase(name)+':'))
	if (required && !('required' in rules)) {
		rules.required = `This field is required.`
	}
	if (maxLength > 0 && !('maxLength' in rules)) {
		rules.maxLength = { value: maxLength, message: `This field doesn't accept more than ${maxLength} characters.` }
	}
	const id = `field-${name}`
	let as
	if (type == 'textarea') {
		as = type
		type = undefined
	} else {
		as = undefined
	}

	const className = fieldProps.className || ''
	delete fieldProps.className //can't we do it all in a single call? there's _.remove() for arrays, but not for objects

	const { register, formState: { errors } } = useFormContext()
	return <Form.Group className={`mb-3 ${className}`.trim()}>
		<Label {...{ id, required }}>{label}</Label>
		<Form.Control {...register(name, rules)} {...{as, type, id, name, defaultValue, maxLength, required, ...fieldProps}}/>
		{errors[name] && <Form.Text className="text-danger">{errors[name].message}</Form.Text>}
	</Form.Group>
}

const mutualPropType = (type, theOther, msg, notBoth) => (props, name) => {
	const prefix = props.name || props.label? `Form Line "${props.name || props.label}"` : 'A Form Line'
	if (typeof props[name] == 'undefined' && typeof props[theOther] == 'undefined') {
		return new Error(`${prefix} ${msg}`)
	}
	if (notBoth && typeof props[name] != 'undefined' && typeof props[theOther] != 'undefined') {
		return new Error(`${prefix} ${msg}, but not both`)
	}
	const actualType = typeof props[name]
	if (actualType != 'undefined' && (actualType != type || (type == 'mixed' && actualType != 'string' && actualType != 'number'))) {
		return new Error(`${prefix} expected ${name} to be ${type}, but received ${actualType} instead`)
	}
}

InputRow.propTypes = {
	label       : mutualPropType('string', 'name', 'is missing a label and/or name'),
	name        : mutualPropType('string', 'label', 'is missing a label and/or name'),
	type        : PropTypes.string,
	defaultValue: PropTypes.any,
	required    : PropTypes.bool,
	maxLength   : PropTypes.number,
	rules       : PropTypes.object,
}
